export interface DroppedFile {
  file: File;
  name: string;
  ext: string;
  /** Present for parseable assets (.fbx/.glb/.gltf); omitted for video files. */
  buffer?: ArrayBuffer;
  /** Present for .json drops (scene / cameras files). */
  text?: string;
}

const MODEL_EXTS = ['fbx', 'glb', 'gltf', 'vrm'];
const VIDEO_EXTS = ['mp4', 'mov', 'm4v', 'webm'];
const TEXT_EXTS = ['json'];
const SUPPORTED = [...MODEL_EXTS, ...VIDEO_EXTS, ...TEXT_EXTS];

/**
 * Wire drag-and-drop on the viewport. Models are read to an ArrayBuffer for
 * parsing; videos are passed through as-is (loaded via object URL) to avoid
 * pulling large files into memory.
 */
export function createDropzone(
  viewport: HTMLElement,
  dropzoneEl: HTMLElement,
  onFile: (file: DroppedFile) => void,
  onError: (message: string) => void
): void {
  let dragDepth = 0;

  const setDrag = (on: boolean): void => {
    dropzoneEl.classList.toggle('drag', on);
  };

  viewport.addEventListener('dragenter', (e) => {
    e.preventDefault();
    dragDepth++;
    setDrag(true);
  });

  viewport.addEventListener('dragover', (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  });

  viewport.addEventListener('dragleave', (e) => {
    e.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) setDrag(false);
  });

  viewport.addEventListener('drop', (e) => {
    e.preventDefault();
    dragDepth = 0;
    setDrag(false);

    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!SUPPORTED.includes(ext)) {
      onError(`Unsupported file type ".${ext}". Drop a .fbx, .glb, .gltf, .json or a video.`);
      return;
    }

    if (VIDEO_EXTS.includes(ext)) {
      onFile({ file, name: file.name, ext });
      return;
    }

    if (TEXT_EXTS.includes(ext)) {
      file
        .text()
        .then((text) => onFile({ file, text, name: file.name, ext }))
        .catch(() => onError(`Failed to read "${file.name}".`));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      onFile({ file, buffer: reader.result as ArrayBuffer, name: file.name, ext });
    };
    reader.onerror = () => onError(`Failed to read "${file.name}".`);
    reader.readAsArrayBuffer(file);
  });
}
